import type { CSSProperties } from 'react';
import { Link, Navigate } from 'react-router';
import { brandPath, campaignPath } from '@brands/shared';
import { useAuth } from '../auth/AuthContext';
import { accentHex, onAccent } from '../lib/accent';
import { Loading } from '../components/Loading';
import { useBrands, useMyCampaigns } from '../data/brands';
import { colors } from '../styles/tokens';

/** Inicio del cliente: sus marcas (cada una con su portal) y campañas sueltas. */
export function CampaignsPage() {
  const { session } = useAuth();
  const brands = useBrands();
  const campaigns = useMyCampaigns();
  if (brands.status === 'loading' || campaigns.status === 'loading') return <Loading />;

  const myBrands = brands.status === 'ready' ? brands.data : [];
  const loose = campaigns.status === 'ready' ? campaigns.data.filter((c) => !c.brandId) : [];
  const host = window.location.host;

  // Un cliente con una sola marca entra directo a su portal.
  if (!session?.isStaff && myBrands.length === 1 && loose.length === 0 && myBrands[0]) {
    return <Navigate to={brandPath(host, myBrands[0].slug)} replace />;
  }

  const tileStyle = (accent: string) =>
    ({
      background: accentHex(accent),
      color: onAccent(accent),
      '--tile-accent': colors.inkSoft,
    }) as CSSProperties;

  return (
    <main className="page">
      <div className="page__intro">
        <h1 className="t-display">{myBrands.length ? 'Tus marcas' : 'Campañas'}</h1>
      </div>

      {myBrands.length === 0 && loose.length === 0 ? (
        <div className="page__pad empty">
          <p className="t-lead">Aún no tienes campañas disponibles.</p>
          <p className="empty__text">
            Si esperabas ver una, pide a tu contacto en Brands&amp;People que te dé acceso con este
            correo: <strong>{session?.email}</strong>.
          </p>
        </div>
      ) : null}

      {myBrands.length ? (
        <div className="mosaic">
          {[...myBrands]
            .sort((a, b) => a.name.localeCompare(b.name, 'es'))
            .map((b) => (
              <Link
                key={b.id}
                to={brandPath(host, b.slug)}
                className="tile tile--w2"
                style={tileStyle(b.accent)}
              >
                <span className="tile__bar t-meta">
                  <span>{b.slug}</span>
                </span>
                <span className="tile__caption">
                  <span className="t-display">{b.name}</span>
                </span>
              </Link>
            ))}
        </div>
      ) : null}

      {loose.length ? (
        <>
          <h2 className="t-nav page__pad">Otras campañas</h2>
          <div className="mosaic">
            {loose.map((c) => (
              <Link
                key={c.id}
                to={
                  c.clientSlug && c.slug ? campaignPath(host, c.clientSlug, c.slug) : `/c/${c.id}`
                }
                className="tile tile--w2"
                style={tileStyle(c.accent)}
              >
                <span className="tile__bar t-meta">
                  <span>{c.clientName}</span>
                </span>
                <span className="tile__caption">
                  <span className="t-title">{c.name}</span>
                </span>
              </Link>
            ))}
          </div>
        </>
      ) : null}
    </main>
  );
}
